/*
 * NIM-4363: bundle builder lines in the Upcart drawer.
 *
 * The builder (NIM-4362) adds plain 15-serving elixir lines tagged with the private property `_build`
 * ("3" | "4" | "6"). Upcart hides underscore properties, so on their own those lines look like stray
 * singles. This script, using only Upcart's documented public API (upcartSubscribeCartLoaded /
 * upcartSubscribeCartUpdated / upcartSubscribeCartOpened, upcartDocumentOrShadowRoot):
 *   - puts a "Your bundle of N" heading, with the real saving on those lines, on the first build line;
 *   - marks every build line ("Part of your bundle"), and while the cart holds a build hides Upcart's
 *     per-line subscription upgrade on EVERY line (the build is a one-time purchase; upgrading any line,
 *     including an app's free-gift line beside it, adds a separate subscription order at checkout);
 *   - when the cart holds fewer eligible elixirs than the build's tier (a line removed or reduced),
 *     says "Add N more elixir(s) to get $X each" with a link back to the builder.
 * Eligible items are counted across the whole cart, whatever their properties, because the automatic
 * discounts count items, not tags. The saving is read from the cart's discount allocations, never computed.
 *
 * Line data comes from /cart.js (Shopify's own shape) after each Upcart cart event. Drawer rows are
 * matched to cart lines in order by the variant id in each row's product link.
 * Docs: https://docs.aftersell.com/upcart/common_upcart_api_uses.md,
 *       https://docs.aftersell.com/upcart/render_cart_in_shadow_dom_setting.md
 */
(function () {
  'use strict';
  if (window.__vhCartBuild) return;

  var MARK = 'data-vh-build';
  var ITEM = '.upcart-public-cart-item';
  var STYLE = [
    '.vh-build-line { box-shadow: inset 3px 0 0 #442027; }',
    '.vh-build-line .upcart-public-component-product-tile__subscription-upgrade, .vh-cart-has-build .upcart-public-component-product-tile__subscription-upgrade { display: none !important; }',
    '.vh-build-head { margin: 0 0 12px; padding: 10px 12px; background: #e9e4da; color: #291406; line-height: 1.35; }',
    '.vh-build-head__title { margin: 0; font-family: "Shango Gothic Medium"; font-size: 14px; text-transform: uppercase; letter-spacing: 0.02em; }',
    '.vh-build-head__save { margin: 2px 0 0; font-family: "LTC Kennerley Pro Italic"; font-size: 14px; }',
    '.vh-build-head__nudge { margin: 8px 0 0; padding-top: 8px; border-top: 1px solid rgba(41, 20, 6, 0.15); font-family: "LTC Kennerley Pro Regular"; font-size: 14px; }',
    '.vh-build-head__nudge a { color: inherit; text-decoration: underline; white-space: nowrap; }',
    '.vh-build-tag { display: inline-block; margin-top: 2px; font-family: "LTC Kennerley Pro Italic"; font-size: 13px; opacity: 0.8; }',
  ].join('\n');

  /* ---------- config (vh-build-config: "3:500,4:600,6:700|SKU,SKU|/products/...") ---------- */

  function parseConfig(raw) {
    var parts = String(raw || '').split('|');
    var tiers = (parts[0] || '')
      .split(',')
      .map(function (t) {
        var p = t.split(':');
        return { items: parseInt(p[0], 10), off: parseInt(p[1], 10) };
      })
      .filter(function (t) { return t.items > 0 && t.off >= 0; })
      .sort(function (a, b) { return a.items - b.items; });
    var skus = (parts[1] || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    return { tiers: tiers, skus: skus, url: (parts[2] || '').trim() };
  }

  /* ---------- pure state from a /cart.js cart ---------- */

  function buildState(cart, cfg) {
    var items = (cart && cart.items) || [];
    var s = { eligible: 0, buildQty: 0, buildSize: 0, save: 0, unit: 0, need: 0, targetEach: 0, lines: [] };
    items.forEach(function (line, index) {
      if (line.sku && cfg.skus.indexOf(line.sku) !== -1) s.eligible += line.quantity;
      var tag = line.properties && line.properties._build;
      if (tag === undefined || tag === null || tag === '') return;
      s.lines.push(index);
      s.buildQty += line.quantity;
      var size = parseInt(tag, 10) || 0;
      if (size > s.buildSize) s.buildSize = size;
      s.save += (line.original_line_price || 0) - (line.final_line_price || 0);
      if (!s.unit) s.unit = line.original_price || 0;
    });
    var off = 0;
    cfg.tiers.forEach(function (t) { if (t.items <= s.buildSize) off = t.off; });
    if (off > 0 && s.eligible < s.buildSize) {
      s.need = s.buildSize - s.eligible;
      s.targetEach = s.unit - off;
    }
    return s;
  }

  function money(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    var whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(whole ? 0 : 2);
    }
  }

  function headHtml(s, cfg) {
    var html = '<p class="vh-build-head__title">Your bundle of ' + s.buildQty + '</p>';
    if (s.save > 0) html += '<p class="vh-build-head__save">You save ' + money(s.save) + '</p>';
    if (s.need > 0) {
      html +=
        '<p class="vh-build-head__nudge">Add ' + s.need + ' more ' + (s.need === 1 ? 'elixir' : 'elixirs') +
        ' to get ' + money(s.targetEach) + ' each. ' +
        (cfg.url ? '<a href="' + cfg.url.replace(/"/g, '&quot;') + '">Back to your bundle</a>' : '') +
        '</p>';
    }
    return html;
  }

  /* ---------- drawer rows -> cart lines ---------- */

  function rowVariant(row) {
    var a = row.querySelector('a[href*="variant="]');
    var m = a && /[?&]variant=(\d+)/.exec(a.getAttribute('href') || '');
    return m ? Number(m[1]) : null;
  }

  // Rows are a subsequence of cart.items in order (Upcart renders cart order); match each row to the
  // next cart line with its variant id. Returns [{row, index}] or null when a row cannot be placed.
  function matchRows(rows, items) {
    var out = [];
    var at = 0;
    for (var r = 0; r < rows.length; r++) {
      var v = rowVariant(rows[r]);
      if (v === null) return null;
      while (at < items.length && Number(items[at].variant_id) !== v) at++;
      if (at >= items.length) return null;
      out.push({ row: rows[r], index: at });
      at++;
    }
    return out;
  }

  /* ---------- render (idempotent: writes only when something differs) ---------- */

  function render(root, cart, cfg) {
    if (!root || !root.querySelectorAll) return false;
    var changed = false;
    if (!root.querySelector('style[' + MARK + ']')) {
      var style = document.createElement('style');
      style.setAttribute(MARK, 'style');
      style.textContent = STYLE;
      (root.head || root).appendChild(style);
      changed = true;
    }
    var s = cart ? buildState(cart, cfg) : null;
    var rows = Array.prototype.slice.call(root.querySelectorAll(ITEM));
    var matched = s && s.lines.length ? matchRows(rows, cart.items) : null;
    var buildRows = [];
    if (matched) {
      matched.forEach(function (m) {
        if (s.lines.indexOf(m.index) !== -1) buildRows.push(m.row);
      });
    }
    var headRow = buildRows[0] || null;
    var headSig = headRow ? JSON.stringify([s.buildQty, s.save, s.need, s.targetEach]) : '';

    var cartHasBuild = !!(s && s.lines.length);
    rows.forEach(function (row) {
      if (row.classList.contains('vh-cart-has-build') !== cartHasBuild) {
        row.classList.toggle('vh-cart-has-build', cartHasBuild);
        changed = true;
      }
      var isBuild = buildRows.indexOf(row) !== -1;
      if (row.classList.contains('vh-build-line') !== isBuild) {
        row.classList.toggle('vh-build-line', isBuild);
        changed = true;
      }
      var tag = row.querySelector('[' + MARK + '="tag"]');
      if (isBuild && !tag) {
        var props = row.querySelector('.upcart-public-component-product-tile__properties');
        if (props) {
          tag = document.createElement('span');
          tag.setAttribute(MARK, 'tag');
          tag.className = 'vh-build-tag';
          tag.textContent = 'Part of your bundle';
          props.appendChild(tag);
          changed = true;
        }
      } else if (!isBuild && tag) {
        tag.remove();
        changed = true;
      }
      var head = row.querySelector('[' + MARK + '="head"]');
      if (row === headRow) {
        if (!head || head.getAttribute('data-sig') !== headSig) {
          if (head) head.remove();
          head = document.createElement('div');
          head.setAttribute(MARK, 'head');
          head.setAttribute('data-sig', headSig);
          head.className = 'vh-build-head';
          head.setAttribute('role', 'status');
          head.innerHTML = headHtml(s, cfg);
          row.insertBefore(head, row.firstChild);
          changed = true;
        }
      } else if (head) {
        head.remove();
        changed = true;
      }
    });
    return changed;
  }

  /* ---------- wiring to Upcart ---------- */

  var script = document.currentScript;
  var cfg = parseConfig(script && script.getAttribute('data-vh-build-config'));
  var lastCart = null;
  var queued = false;
  var fetchSeq = 0;

  function root() {
    return window.upcartDocumentOrShadowRoot || null;
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      render(root(), lastCart, cfg);
    });
  }

  function refresh() {
    var seq = ++fetchSeq;
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(base + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart || seq !== fetchSeq) return;
        lastCart = cart;
        schedule();
      })
      .catch(function () {});
  }

  var observed = null;
  function observe() {
    var r = root();
    if (!r || r === observed || typeof MutationObserver === 'undefined') return;
    observed = r;
    new MutationObserver(schedule).observe(r, { childList: true, subtree: true });
  }

  var subscribed = false;
  function subscribe() {
    if (subscribed || typeof window.upcartSubscribeCartUpdated !== 'function') return false;
    subscribed = true;
    window.upcartSubscribeCartLoaded(function () { observe(); refresh(); });
    window.upcartSubscribeCartUpdated(function () { observe(); refresh(); });
    window.upcartSubscribeCartOpened(function () { observe(); schedule(); });
    return true;
  }

  window.__vhCartBuild = { parseConfig: parseConfig, buildState: buildState, render: render, matchRows: matchRows, money: money };

  // Upcart's embed loads on its own schedule; wait for its API (up to ~20 s), then for its root.
  var tries = 0;
  (function wait() {
    if (subscribe()) {
      observe();
      refresh();
      return;
    }
    if (++tries < 100) setTimeout(wait, 200);
  })();
})();
